import { useMemo } from "react";
import { ConnectProto, toJson } from "@racoon/api";
import { useGetGovDailySummariesQuery } from "~/shared/api/govDash/govDash.api";
import {
  parseLocalDateParam,
  startOfLocalDay,
  toTimestamp,
} from "~/shared/utils/filterDateFormatter";

type DailySummaryJson = ConnectProto.DailySummaryJson;

// Keyed by index ("0", "1", …) — the shape the previous Python endpoint returned,
// kept so every consumer can stay on `Object.values(data)`.
export type DailySummaryListResponse = Record<string, DailySummaryJson>;

export interface DailySummaryListParams {
  owner_organization_id?: string | number | null;
  district_organization_ids?: string[] | null;
  /** Local calendar day, `YYYY-MM-DD`. */
  date_from?: string | null;
  /** Local calendar day, `YYYY-MM-DD`. */
  date_to?: string | null;
  /**
   * Splits each organization's row per district and stamps the district on it.
   * Left off by default: one row per (parent organization, date), which is what
   * every caller here aggregates over.
   */
  group_by_district?: boolean;
}

/**
 * The RPC's window lands one calendar day earlier than the day asked for, so
 * every bound is sent as the day AFTER the one the caller wants.
 *
 * `FindByOrganizationsAndReportDateRange` (go-backend
 * `internal/repository/daily_summary_repository.go`) floors both ends to the
 * Almaty calendar day and then renders them with `.UTC()` — and Almaty midnight
 * rendered as UTC is 19:00 on the *previous* date. Compared against the `date`
 * typed `report_date` column that lower bound reads as the previous day, so a
 * request for the Almaty day D comes back with D-1. Confirmed against the
 * endpoints directly: the Python `daily_summary/list` rows for 2026-09-01 are
 * what this RPC answers with for 2026-09-02.
 *
 * Midday, not midnight, so neither end sits on a boundary that the flooring
 * could round the wrong way.
 *
 * This is a shim for the backend's off-by-one, NOT a timezone conversion —
 * delete it (and go back to sending the plain local-day bounds) once the
 * repository compares Almaty-day dates instead of `.UTC()` instants.
 */
const goDayProbe = (day: Date): Date => {
  const probe = startOfLocalDay(day);
  probe.setDate(probe.getDate() + 1);
  probe.setHours(12, 0, 0, 0);
  return probe;
};

const toBigIntOrNull = (value: string | number | bigint): bigint | null => {
  try {
    return BigInt(value);
  } catch {
    return null;
  }
};

/**
 * Daily summaries from the Go `GetGovDailySummaries` RPC.
 *
 * Replaces the Python `/v1/platform/daily_summary/list` endpoint, which
 * answered with one row per (MVO × district) and forced callers to re-aggregate.
 * The RPC rolls branches up into their parent organization by default, so a
 * caller gets one row per organization per date — pass `group_by_district` to
 * get the split back.
 *
 * The RPC hands back proto-es messages (camelCase, bigint ids); `toJson`
 * converts each row to its `DailySummaryJson` form so the snake_case field names
 * every consumer already reads keep working.
 */
export const useGetDailySummaryListQuery = (
  params: DailySummaryListParams,
  options?: { skip?: boolean },
) => {
  const { owner_organization_id, district_organization_ids, date_from, date_to } = params;

  const arg = useMemo(() => {
    // Parsed inside the memo so the dependency list can stay on the raw
    // `YYYY-MM-DD` strings: the parsed `Date`s are new objects every render and
    // would defeat the memo if they were dependencies themselves.
    //
    // `parseLocalDateParam` reads `YYYY-MM-DD` as a *local* day — `new Date(str)`
    // would read it as UTC and shift it — and rejects impossible dates rather than
    // letting `new Date(2026, 1, 30)` roll silently into March.
    const from = parseLocalDateParam(date_from ?? undefined);
    const to = parseLocalDateParam(date_to ?? undefined);
    if (!from || !to) return undefined;
    const serviceProviderId =
      owner_organization_id != null ? toBigIntOrNull(owner_organization_id) : null;
    const districtIds = (district_organization_ids ?? [])
      .map(toBigIntOrNull)
      .filter((id): id is bigint => id !== null);

    return {
      filter: {
        districtOrganizationIds: districtIds,
        serviceProviderIds: serviceProviderId !== null ? [serviceProviderId] : [],
      },
      fromTo: {
        from: toTimestamp(goDayProbe(from)),
        to: toTimestamp(goDayProbe(to)),
      },
      groupByDistrict: params.group_by_district ?? false,
    };
  }, [
    owner_organization_id,
    (district_organization_ids ?? []).join(","),
    date_from,
    date_to,
    params.group_by_district,
  ]);

  const result = useGetGovDailySummariesQuery(
    arg as Parameters<typeof useGetGovDailySummariesQuery>[0],
    {
      // `from_to` is required on the request, so without both dates there is
      // nothing valid to ask for.
      skip: options?.skip || !arg,
    },
  );

  const data = useMemo<DailySummaryListResponse | undefined>(() => {
    if (!result.data?.dailySummaries) return undefined;
    return Object.fromEntries(
      result.data.dailySummaries.map((summary, index) => [
        String(index),
        toJson(ConnectProto.DailySummarySchema, summary),
      ]),
    );
  }, [result.data]);

  return { ...result, data };
};
